// Transaction Wizard — runs kept on the DEVICE (design §9 / §15, Phase 4).
//
// A `suspend: "memory"` run normally lives only in this tab (runStore.ts): a
// reload loses it, by design, so an old draft never silently comes back
// against a record that has since changed. An OFFLINE wizard cannot work that
// way — a handheld in a warehouse is reloaded, killed by the OS or runs out of
// battery mid-pick, and there is no server run store to fall back on (offline
// wizards may not use `suspend: "server"`; the publish gate refuses it).
//
// So for a wizard marked `offline: true` (or any run while the host reports it
// is offline) the runner mirrors every committed run here, in the device's own
// storage, and drops it when the run finishes or is cancelled. Before it looks
// for a run to continue, the runner `hydrate`s this store into the memory
// store — after which the ordinary resume path (`memoryRunFor` /
// `readMemoryRun`, then `resumeRun`, which restarts a frame whose model
// changed) does the rest, unchanged.
//
// localStorage, every access wrapped: a private window, cleared site data or a
// full quota must cost the user their safety net, never their run.

import type { WizardRun } from './contract'
import { readMemoryRun, writeMemoryRun } from './runStore'

const PREFIX = 'twasta.wizard.device-run:'
/** Matches the server store's default expiry (§9.1). */
const MAX_AGE_MS = 7 * 24 * 3600 * 1000

interface Stored {
  slot: string
  run: WizardRun
  saved_at: number
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
  } catch {
    return null
  }
}

/** One key per launch identity: a restarted run replaces the old one. */
function keyFor(project: string, slot: string): string {
  return `${PREFIX}${project}\u0000${slot}`
}

function isFinished(run: WizardRun): boolean {
  return run.status === 'completed' || run.status === 'cancelled' || run.status === 'expired'
}

/** Mirror a committed run (or forget it once it finished). Never throws. */
export function syncDeviceRun(project: string, run: WizardRun, slot: string): void {
  const st = storage()
  if (!st || !project || !slot) return
  const key = keyFor(project, slot)
  try {
    if (isFinished(run)) { st.removeItem(key); return }
    const value: Stored = { slot, run, saved_at: Date.now() }
    st.setItem(key, JSON.stringify(value))
  } catch (err) {
    // Quota / denied: the run carries on in memory; say so once on the console.
    if (typeof console !== 'undefined') console.warn('[wizard] device storage unavailable:', err)
  }
}

/** Forget the device copy of a launch identity. Never throws. */
export function dropDeviceRun(project: string, slot: string): void {
  try { storage()?.removeItem(keyFor(project, slot)) } catch { /* nothing to drop */ }
}

/** Every unfinished run this device holds for a project (newest first);
 *  expired or unreadable entries are removed on the way. */
export function listDeviceRuns(project: string): Array<{ slot: string; run: WizardRun; savedAt: number }> {
  const st = storage()
  if (!st) return []
  const prefix = `${PREFIX}${project}\u0000`
  const out: Array<{ slot: string; run: WizardRun; savedAt: number }> = []
  const stale: string[] = []
  try {
    for (let i = 0; i < st.length; i++) {
      const k = st.key(i)
      if (!k || !k.startsWith(prefix)) continue
      try {
        const v = JSON.parse(st.getItem(k) || 'null') as Stored | null
        if (!v?.run?.run_id || !Array.isArray(v.run.stack) || isFinished(v.run)
          || Date.now() - Number(v.saved_at || 0) > MAX_AGE_MS) {
          stale.push(k)
          continue
        }
        out.push({ slot: v.slot, run: v.run, savedAt: Number(v.saved_at || 0) })
      } catch {
        stale.push(k)
      }
    }
    for (const k of stale) st.removeItem(k)
  } catch {
    return out
  }
  return out.sort((a, b) => b.savedAt - a.savedAt)
}

/** Load the device's runs into the in-tab memory store, so the runner's
 *  ordinary resume path finds them. A run already in memory wins (it is the
 *  newer of the two). Returns how many were restored. */
export function hydrateDeviceRuns(project: string): number {
  let n = 0
  for (const { slot, run } of listDeviceRuns(project)) {
    if (readMemoryRun(run.run_id)) continue
    writeMemoryRun(run, slot)
    n += 1
  }
  return n
}
